<template>
  <div>
    <Card
      v-bind="$attrs"
      :clickable="isClickable"
      :selected="selected"
      :href="profileUrl"
      :target="target"
      :custom-classes="customClasses"
    >
      <AlertBanner v-if="availability" :status="validStatus" :auto-scroll-to-banner="false">
        <template #icon>
          <CalendarIcon />
        </template>
        Available: {{ availabilityText }}
      </AlertBanner>
      <AlertBanner v-if="shouldShowAvailabilityAlert" class="mt-8 mb-16" :status="6" :auto-scroll-to-banner="false">
        <div>You invited this sitter to apply</div>
      </AlertBanner>
      <div v-if="isLoading"><SitterCardLoadingSkeleton /></div>
      <div v-else>
        <div class="flex flex-col">
          <div class="w-full flex flex-row">
            <div class="w-full pr-16 overflow-hidden">
              <h2 class="text-h3 text-ellipsis overflow-hidden whitespace-nowrap">{{ name }}</h2>
              <p v-if="professionalExperienceDisplay" class="text-body">{{ professionalExperienceDisplay }}</p>
              <p v-if="informalExperienceDisplay" class="text-body">{{ informalExperienceDisplay }}</p>
              <p class="text-body">{{ locationText }}</p>
              <p v-if="hasRate" class="text-body">
                <span class="profile-card__rate-unit">$</span>
                <span class="profile-card__rate-value">{{ computedMin }}–{{ computedMax }}</span
                >/hr
              </p>
              <StarRating v-if="totalRatings > 0" :count="totalRatings || 0" :average="avgRating" />
            </div>
            <div>
              <router-link
                v-if="photoUploadAvatar"
                class="relative"
                aria-label="Photo upload"
                :to="{ name: 'provider-photo-upload', query: { profileType: photoProfileType } }"
              >
                <Avatar photo-upload-icon :size="AvatarSize.MEGA" name="" empty-alt />
                <div class="flex justify-center items-center w-32 h-32 absolute top-14 right-0" aria-label="Add profile photo">
                  <AddFilled />
                </div>
              </router-link>
              <Avatar v-else :name="name" :image-url="photoUrl" :size="AvatarSize.MEGA" />
            </div>
          </div>
          <p
            v-if="truncatedDescription"
            id="sitter-card-description"
            class="text-small border-t-2 border-solid border-grey-divider-subdued pt-4"
          >
            {{ truncatedDescription }}
          </p>
          <div class="flex flex-wrap items-center gap-y-12 md:gap-16 mt-16">
            <SitterBadgeList :include-inactive-badges="false" :badge-size="40" :idv="idv" :badges="visibleBadges" />
            <Button
              v-if="shouldShowInviteButton"
              value="Invite to Apply"
              variant="primary"
              size="small"
              class="whitespace-nowrap ml-auto max-[410px]:ml-0 max-[410px]:w-full"
              @click="openInvitePopup"
            />
          </div>
        </div>
      </div>
    </Card>
    <!-- Popup is outside Card to prevent click propagation issues -->
    <RecruitSitterPopup
      v-if="account && (shouldShowInviteButton || showInvitePopup)"
      v-model="showInvitePopup"
      :account="account"
      :sitter-uuid="sitterIdForProfile"
      :sitter-name="name"
      :search-care-type="searchCareType || caretype"
      :parent-jobs="parentJobs"
      @sitter-recruited="handleSitterRecruited"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance, ref } from 'vue';
import { parseISO, format } from 'date-fns';
import pluralize from 'pluralize';
import { isChildCare } from '@/apps/provider/profile/__types__/CareTypes';
import helperStatus from '../../helpers/status';
import type { IDV, Badge } from '../sitter_badge_list/__types__/SitterBadgeListTypes';
import AlertBanner from '../alert_banner/AlertBanner.vue';
import Avatar, { AvatarSize } from '../avatar/Avatar.vue';
import Card from '../card/Card.vue';
import SitterCardLoadingSkeleton from './SitterCardLoadingSkeleton.vue';
import StarRating from '../star_rating/StarRating.vue';
import SitterBadgeList from '../sitter_badge_list/SitterBadgeList.vue';
import Button from '../button/Button.vue';
import RecruitSitterPopup from '@/apps/search/features/RecruitSitterPopup.vue';
import CalendarIcon from '../../icons/CalendarIcon.vue';
import AddFilled from '../../icons/AddFilled.vue';
import type { LinkTarget } from '../../helpers/redirects';
import getProviderProfileUrl from '@/helpers/providerProfileUrl';
import type { Account } from '@/models/account';
import type { JobEdge } from '@/graphql/search/queries';
import { CareType } from '@/types/care_type';

defineOptions({ name: 'SitterCard', inheritAttrs: false });

const props = withDefaults(
  defineProps<{
    name?: string;
    city?: string;
    stateAbbr?: string;
    description?: string;
    avgRating?: number | null;
    minRate?: number | null;
    maxRate?: number | null;
    distance?: number | null;
    totalRatings?: number | null;
    photoUrl?: string;
    yearsExperience?: number | null;
    informalYearsExperience?: number | null;
    idv?: IDV;
    badges?: Badge[];
    sitterIdForProfile?: string | null;
    caretype?: string;
    availability?: { start: string; end: string } | null;
    isLoading?: boolean;
    customClasses?: string;
    photoUploadAvatar?: boolean;
    photoProfileType?: CareType;
    target?: LinkTarget;
    account?: Account | null;
    showInviteButton?: boolean;
    isRecruited?: boolean;
    searchCareType?: string;
    parentJobs?: JobEdge[];
    selected?: boolean;
  }>(),
  {
    name: '',
    city: '',
    stateAbbr: '',
    description: '',
    avgRating: null,
    minRate: null,
    maxRate: null,
    distance: null,
    totalRatings: null,
    photoUrl: '',
    yearsExperience: null,
    informalYearsExperience: null,
    idv: () => ({}) as IDV,
    badges: () => [],
    sitterIdForProfile: null,
    caretype: 'babysitter',
    availability: null,
    isLoading: false,
    customClasses: '',
    photoUploadAvatar: false,
    photoProfileType: CareType.BABYSITTER,
    target: '_self',
    account: null,
    showInviteButton: false,
    isRecruited: false,
    searchCareType: '',
    parentJobs: () => [],
    selected: false,
  }
);

const emit = defineEmits<{ (event: 'sitterRecruited', sitterUuid: string | null): void }>();
const METERS_TO_MILES_CONVERSION = 1609.344;
const LIMIT_WORD_COUNT = 300;
const instance = getCurrentInstance();
const showInvitePopup = ref(false);
const locallyRecruited = ref(false);

const effectiveIsRecruited = computed(() => props.isRecruited || locallyRecruited.value);
const isClickable = computed(() => !!props.sitterIdForProfile);
const isAvailabilityRoute = computed(() => instance?.proxy?.$route?.path === '/availability/search');
const shouldShowAvailabilityAlert = computed(
  () => effectiveIsRecruited.value && (isAvailabilityRoute.value || props.showInviteButton)
);
const isChildCareProfile = computed(() => isChildCare(props.caretype));
const yearsExperiencePluralized = computed(() =>
  props.yearsExperience === 1 ? '1 year experience' : `${props.yearsExperience} years experience`
);
const professionalExperienceDisplay = computed(() => {
  const experience = props.yearsExperience ?? 0;
  if (isChildCareProfile.value) {
    if (experience < 1) return '';
    return `${experience} ${pluralize('year', experience)} professional experience`;
  }
  return experience ? yearsExperiencePluralized.value : '';
});
const informalExperienceDisplay = computed(() => {
  if (!isChildCareProfile.value) return '';
  const experience = props.informalYearsExperience ?? 0;
  return experience < 1 ? '' : `${experience} ${pluralize('yr', experience)} informal experience`;
});
const computedDistance = computed(() => {
  if (typeof props.distance !== 'number') return '';
  const miles = Math.round(props.distance / METERS_TO_MILES_CONVERSION);
  if (miles < 1) return '< 1 mile';
  return `${miles} ${pluralize('mile', miles)}`;
});
const locationText = computed(() => {
  const distanceSeparator = (props.city || props.stateAbbr) && computedDistance.value ? ' • ' : '';
  const cityStateSeparator = props.city && props.stateAbbr ? ', ' : '';
  return `${props.city}${cityStateSeparator}${props.stateAbbr}${distanceSeparator}${computedDistance.value}`;
});
const truncatedDescription = computed(() => {
  if (!props.description) return '';
  const regex = new RegExp(`^(.{${LIMIT_WORD_COUNT}}[^\\s]*).*`);
  const truncated = props.description.replace(/(\r\n|\n|\r)/gm, '').replace(regex, '$1');
  return `${truncated}...`;
});
const computedMin = computed(() => Math.floor((props.minRate ?? 0) / 100));
const computedMax = computed(() => Math.ceil((props.maxRate ?? 0) / 100));
const hasRate = computed(() => computedMin.value > 0 && computedMax.value > 0);
const profileUrl = computed(() =>
  props.sitterIdForProfile ? getProviderProfileUrl(props.sitterIdForProfile, props.caretype) || false : false
);
const availabilityText = computed(() => {
  if (!props.availability) return '';
  const start = parseISO(props.availability.start);
  const end = parseISO(props.availability.end);
  return `${format(start, 'MMM d, h:mm aaa')} – ${format(end, 'h:mm aaa')}`;
});
const validStatus = helperStatus.Valid;
const visibleBadges = computed(() => {
  const allBadgesComplete = props.badges.every((badge) => badge.status === 'COMPLETE');
  return allBadgesComplete && props.idv?.verdict === 'passed'
    ? props.badges.filter((badge) => badge.name !== 'basic_criminal')
    : props.badges;
});
const shouldShowInviteButton = computed(() => props.showInviteButton && !effectiveIsRecruited.value && !!props.account);

function openInvitePopup(event: Event): void {
  event.preventDefault();
  event.stopPropagation();
  showInvitePopup.value = true;
}

function handleSitterRecruited(sitterUuid: string | null = null): void {
  locallyRecruited.value = true;
  emit('sitterRecruited', sitterUuid || props.sitterIdForProfile || null);
}
</script>

<style lang="scss" scoped>
#sitter-card-description {
  word-wrap: break-word;
}
</style>
